// Serialize / parse (parse-don't-validate on restore).
//
// Split out of `Navigation/paneTree.ts` (C1 hotspot decomposition). The inverse
// pair `viewState.ts` folds in. `serializePaneTree` is a straightforward JSON
// projection (dirty is NOT persisted — every restored tab is clean, the existing
// rule). `parsePaneTree` re-establishes every invariant at the boundary so ANY
// earlier build's blob degrades safely, never a crash.

import type { ResourceRef } from "../types.js";
import {
    LEGACY_AMBIENT_CONVERSATION_ID,
    nextConversationId,
} from "../types.js";
import { keyOf, keyStr } from "./keys.js";
import { findLeaf, leavesOf, normalizeSizes, seedPaneTree } from "./tree.js";
import {
    MAX_PANE_DEPTH,
    type Orientation,
    type Pane,
    type PaneId,
    type PaneTree,
    type SerializedPaneTree,
    type Tab,
    type TabKey,
} from "./types.js";

const serTab = (tab: Tab): unknown =>
    tab.type === "resource"
        ? { type: "resource", resource: tab.resource, dirty: false }
        : tab.type === "conversation"
          ? { type: "conversation", id: tab.id }
          : { type: tab.type };

const serPane = (p: Pane): unknown =>
    p.type === "leaf"
        ? {
              type: "leaf",
              id: p.id,
              // Quality reports are ephemeral — never persisted, so they don't
              // come back on reload (active-ness degrades to the first surviving
              // tab; a restored session reopens a report from the material's
              // quality panel; not persisting also sidesteps re-sourcing a stale
              // material's evaluation). Conversation tabs (nascent or durable)
              // ARE persisted like ordinary tabs, so a working set of
              // conversations + resources (and any glance) round-trips
              // faithfully; a reloaded nascent conversation reopens as an empty
              // composer (its draft + pending grounding are shell-local, not
              // persisted — hero-is-initial-conversation-state §6.3).
              tabs: p.tabs.filter((t) => t.type !== "quality").map(serTab),
              // D7: persist the slot AS a preview (a bare ref). Restored as a
              // preview by `parsePane`; a malformed blob degrades to null there.
              preview: p.preview,
              active: p.active,
          }
        : {
              type: "branch",
              id: p.id,
              orientation: p.orientation,
              children: p.children.map(serPane),
              sizes: p.sizes,
          };

export const serializePaneTree = (t: PaneTree): SerializedPaneTree => ({
    root: serPane(t.root),
    focused: t.focused,
});

// One stored resource ref → the typed sum (shape only; existence is the host's
// reconcile, not this boundary — impl doc §3.4).
const parseResourceRef = (v: unknown): ResourceRef | undefined => {
    if (typeof v !== "object" || v === null) return undefined;
    if (!("type" in v && "id" in v && "title" in v)) return undefined;
    const { type, id, title } = v;
    if (typeof id !== "string" || typeof title !== "string") return undefined;
    switch (type) {
        case "source":
        case "material":
        case "export":
        case "file":
            return { type, id, title };
        default:
            return undefined;
    }
};

const parseTab = (v: unknown): Tab | undefined => {
    if (typeof v !== "object" || v === null || !("type" in v)) return undefined;
    switch (v.type) {
        case "conversation":
            return {
                type: "conversation",
                id:
                    "id" in v && typeof v.id === "string"
                        ? v.id
                        : LEGACY_AMBIENT_CONVERSATION_ID,
            };
        case "settings":
            return { type: "settings" };
        case "home":
            return { type: "home" };
        case "studio":
            return { type: "studio" };
        case "projects":
            return { type: "projects" };
        case "knowledge":
            return { type: "knowledge" };
        case "search":
            return { type: "search" };
        case "resource": {
            const r =
                "resource" in v ? parseResourceRef(v.resource) : undefined;
            return r === undefined
                ? undefined
                : { type: "resource", resource: r, dirty: false };
        }
        default:
            return undefined;
    }
};

// The stored active key, re-anchored to the parsed tabs (and the restored
// preview): anything not naming a surviving tab or the preview degrades to the
// first tab (the leaf-active invariant, widened to admit the preview slot).
const parseActive = (
    v: unknown,
    tabs: readonly Tab[],
    preview: ResourceRef | null,
): TabKey => {
    const first = tabs[0];
    // `parsePane` returns null for an empty leaf, so `first` is defined in
    // practice; the seed-conversation fallback keeps the type total for the
    // empty case.
    const fallback: TabKey =
        first === undefined
            ? { type: "conversation", id: nextConversationId([]) }
            : keyOf(first);
    if (typeof v !== "object" || v === null || !("type" in v)) return fallback;
    if (
        v.type === "conversation" &&
        tabs.some(
            (t) =>
                t.type === "conversation" &&
                t.id ===
                    ("id" in v && typeof v.id === "string"
                        ? v.id
                        : LEGACY_AMBIENT_CONVERSATION_ID),
        )
    )
        return {
            type: "conversation",
            id:
                "id" in v && typeof v.id === "string"
                    ? v.id
                    : LEGACY_AMBIENT_CONVERSATION_ID,
        };
    if (v.type === "settings" && tabs.some((t) => t.type === "settings"))
        return { type: "settings" };
    if (v.type === "home" && tabs.some((t) => t.type === "home"))
        return { type: "home" };
    if (v.type === "studio" && tabs.some((t) => t.type === "studio"))
        return { type: "studio" };
    if (v.type === "projects" && tabs.some((t) => t.type === "projects"))
        return { type: "projects" };
    if (v.type === "knowledge" && tabs.some((t) => t.type === "knowledge"))
        return { type: "knowledge" };
    if (v.type === "search" && tabs.some((t) => t.type === "search"))
        return { type: "search" };
    if (v.type === "resource" && "id" in v && typeof v.id === "string") {
        const id = v.id;
        if (tabs.some((t) => t.type === "resource" && t.resource.id === id))
            return { type: "resource", id };
        if (preview !== null && preview.id === id)
            return { type: "resource", id }; // a restored active glance survives
    }
    return fallback;
};

const parsePane = (
    v: unknown,
    mint: () => PaneId,
    depth: number,
): Pane | null => {
    if (typeof v !== "object" || v === null || !("type" in v)) return null;
    if (v.type === "leaf") {
        const id = "id" in v && typeof v.id === "string" ? v.id : mint();
        const raw = "tabs" in v && Array.isArray(v.tabs) ? v.tabs : [];
        const seen = new Set<string>();
        const tabs: Tab[] = [];
        for (const rt of raw) {
            const tab = parseTab(rt);
            if (tab === undefined) continue;
            const k = keyStr(keyOf(tab));
            if (seen.has(k)) continue; // dedup within the leaf
            seen.add(k);
            tabs.push(tab);
        }
        if (tabs.length === 0) return null;
        // D7: restore the slot AS a preview; an absent/malformed ref degrades to
        // null (never a promoted kept tab). Dedup: a ref already KEPT must not
        // also glance (the preview.id∉tabs invariant).
        const rawPreview =
            "preview" in v ? parseResourceRef(v.preview) : undefined;
        const preview =
            rawPreview === undefined ||
            tabs.some(
                (t) => t.type === "resource" && t.resource.id === rawPreview.id,
            )
                ? null
                : rawPreview;
        const active = parseActive(
            "active" in v ? v.active : undefined,
            tabs,
            preview,
        );
        return { type: "leaf", id, tabs, preview, active };
    }
    if (v.type === "branch") {
        const raw =
            "children" in v && Array.isArray(v.children) ? v.children : [];
        if (depth >= MAX_PANE_DEPTH) {
            // Degrade an over-deep branch to its first parseable leaf.
            for (const k of raw) {
                const p = parsePane(k, mint, depth);
                const leaf = p === null ? undefined : leavesOf(p)[0];
                if (leaf !== undefined) return leaf;
            }
            return null;
        }
        const kids = raw
            .map((k) => parsePane(k, mint, depth + 1))
            .filter((p): p is Pane => p !== null);
        if (kids.length === 0) return null;
        if (kids.length === 1) return kids[0] ?? null; // collapse
        const orientation: Orientation =
            "orientation" in v && v.orientation === "column" ? "column" : "row";
        const rawSizes =
            "sizes" in v && Array.isArray(v.sizes)
                ? v.sizes.filter((n): n is number => typeof n === "number")
                : [];
        const sizes =
            rawSizes.length === kids.length
                ? normalizeSizes(rawSizes)
                : kids.map(() => 1 / kids.length);
        const id = "id" in v && typeof v.id === "string" ? v.id : mint();
        return { type: "branch", id, orientation, children: kids, sizes };
    }
    return null;
};

export const parsePaneTree = (raw: unknown): PaneTree => {
    let counter = 0;
    const mint = (): PaneId => `pane-${counter++}`;
    if (typeof raw !== "object" || raw === null || !("root" in raw)) {
        return seedPaneTree(mint());
    }
    const root = parsePane(raw.root, mint, 0);
    if (root === null) return seedPaneTree(mint());
    const stored =
        "focused" in raw && typeof raw.focused === "string"
            ? raw.focused
            : undefined;
    const focused =
        stored !== undefined && findLeaf(root, stored) !== undefined
            ? stored
            : (leavesOf(root)[0]?.id ?? mint());
    return { root, focused };
};
